import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, Download, FileCheck, ExternalLink } from 'lucide-react';
import { DocumentoLegal } from '@/hooks/useDocumentosLegales';
import CumplimientoForm from './CumplimientoForm';

interface DocumentViewerProps {
  documento: DocumentoLegal | null;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentViewer = ({ documento, isOpen, onClose }: DocumentViewerProps) => {
  const [activeTab, setActiveTab] = useState('viewer');

  if (!documento) return null;

  const handleViewDocument = () => {
    window.open(documento.url_archivo, '_blank');
  };

  const handleDownloadDocument = () => {
    const link = document.createElement('a');
    link.href = documento.url_archivo;
    link.download = documento.nombre_archivo;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileCheck className="w-5 h-5" />
            {documento.nombre_documento}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {documento.tipo_documento} • {documento.nombre_archivo}
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
              <TabsTrigger value="viewer">Visualización</TabsTrigger>
              <TabsTrigger value="cumplimiento">Cumplimiento</TabsTrigger>
            </TabsList>

            <TabsContent value="viewer" className="flex-1 overflow-hidden">
              <div className="h-full flex flex-col space-y-4">
                {/* Action Buttons */}
                <div className="flex gap-2 p-4 bg-muted/30 rounded-lg">
                  <Button 
                    onClick={handleViewDocument}
                    className="flex-1"
                    variant="default"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver en Navegador
                  </Button>
                  <Button 
                    onClick={handleDownloadDocument}
                    className="flex-1"
                    variant="outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar
                  </Button>
                  <Button 
                    onClick={handleViewDocument}
                    variant="outline"
                    size="sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>

                {/* PDF Viewer */}
                <div className="flex-1 border rounded-lg overflow-hidden bg-muted/20">
                  <iframe
                    src={`${documento.url_archivo}#view=FitH&toolbar=1&navpanes=1&scrollbar=1&page=1&zoom=page-width`}
                    className="w-full h-full min-h-[500px] border-0"
                    title={documento.nombre_documento}
                    allow="fullscreen"
                    loading="lazy"
                    onError={(e) => {
                      console.error('Error cargando PDF en iframe:', e);
                      // Si el iframe falla, abrimos en nueva pestaña
                      window.open(documento.url_archivo, '_blank');
                    }}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="cumplimiento" className="flex-1 min-h-0">
              <div className="h-full overflow-y-auto">
                <CumplimientoForm documento={documento} />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewer;